import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../constants/urls';
import OverviewCards from './OverviewCards';
import TEStatsTable from './TEStatsTable';
import CEStatsTable from './CEStatsTable';
import ArticleOverviewTable from './ArticleOverviewTable';
import DateSelector from './DateSelector';
import ExportButtons from './ExportButtons';
import './reports.css';

const ReportsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    start_date: null,
    end_date: null
  });
  const [overviewData, setOverviewData] = useState(null);
  const [teStats, setTeStats] = useState([]);
  const [ceStats, setCeStats] = useState([]);
  const [weeklyStats, setWeeklyStats] = useState([]);
  const [articleOverview, setArticleOverview] = useState([]);

  // Fetch all reports data
  const fetchReportsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query string for date range
      const queryParams = new URLSearchParams();
      if (dateRange.start_date) {
        queryParams.append('start_date', dateRange.start_date);
      }
      if (dateRange.end_date) {
        queryParams.append('end_date', dateRange.end_date);
      }

      const queryString = queryParams.toString();

      // Fetch all data in parallel
      const [overviewRes, teStatsRes, ceStatsRes, weeklyStatsRes, articleOverviewRes] = await Promise.all([
        fetch(`${API_BASE}/api/reports/overview${queryString ? '?' + queryString : ''}`, {
          credentials: 'include'
        }),
        fetch(`${API_BASE}/api/reports/te-stats${queryString ? '?' + queryString : ''}`, {
          credentials: 'include'
        }),
        fetch(`${API_BASE}/api/reports/ce-stats${queryString ? '?' + queryString : ''}`, {
          credentials: 'include'
        }),
        fetch(`${API_BASE}/api/reports/weekly-stats${queryString ? '?' + queryString : ''}`, {
          credentials: 'include'
        }),
        fetch(`${API_BASE}/api/reports/article-overview${queryString ? '?' + queryString : ''}`, {
          credentials: 'include'
        })
      ]);

      // Check for errors
      if (!overviewRes.ok) throw new Error('Failed to fetch overview data');
      if (!teStatsRes.ok) throw new Error('Failed to fetch TE stats');
      if (!ceStatsRes.ok) throw new Error('Failed to fetch CE stats');
      if (!articleOverviewRes.ok) throw new Error('Failed to fetch article overview');
      // Weekly stats is optional, don't throw error if it fails

      // Parse responses
      const [overviewData, teStatsData, ceStatsData, articleOverviewData] = await Promise.all([
        overviewRes.json(),
        teStatsRes.json(),
        ceStatsRes.json(),
        articleOverviewRes.json()
      ]);

      // Parse weekly stats if successful
      let weeklyStatsData = [];
      if (weeklyStatsRes.ok) {
        const weeklyData = await weeklyStatsRes.json();
        weeklyStatsData = weeklyData.data || [];
      }

      // Set state
      setOverviewData(overviewData.data);
      setTeStats(teStatsData.data || []);
      setCeStats(ceStatsData.data || []);
      setWeeklyStats(weeklyStatsData);
      setArticleOverview(articleOverviewData.data || []);

    } catch (err) {
      console.error('Error fetching reports data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchReportsData();
  }, [dateRange]);

  // Handle date range change
  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchReportsData();
  };

  if (loading) {
    return (
      <div className="reports-dashboard">
        <div className="reports-header">
          <h1>Reports Dashboard</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading reports data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reports-dashboard">
        <div className="reports-header">
          <h1>Reports Dashboard</h1>
        </div>
        <div className="error-container">
          <div className="error-icon">!</div>
          <h3>Error Loading Reports</h3>
          <p>{error}</p>
          <button className="retry-button" onClick={handleRefresh}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-dashboard">
      <div className="reports-header">
        <h1>Reports Dashboard</h1>
        <div className="header-actions">
          <DateSelector 
            onDateRangeChange={handleDateRangeChange}
            currentRange={dateRange}
          />
          <button className="refresh-button" onClick={handleRefresh}>
            Refresh
          </button>
        </div>
      </div>

      {overviewData && (
        <div className="overview-section">
          <h2>Overview</h2>
          <OverviewCards data={overviewData} />
        </div>
      )}

      <div className="stats-sections">
        <div className="te-stats-section">
          <div className="section-header">
            <h2>TE Performance Stats</h2>
            <ExportButtons 
              type="te" 
              dateRange={dateRange}
              data={teStats}
            />
          </div>
          <TEStatsTable data={teStats} />
        </div>

        <div className="ce-stats-section">
          <div className="section-header">
            <h2>CE Performance Stats</h2>
            <ExportButtons 
              type="ce" 
              dateRange={dateRange}
              data={ceStats}
            />
          </div>
          <CEStatsTable data={ceStats} />
        </div>
      </div>

      <div className="article-overview-section">
        <div className="section-header">
          <h2>Article Processing Overview</h2>
          <div className="section-info">
            <p>Detailed view of all articles with processing dates and links</p>
          </div>
        </div>
        <ArticleOverviewTable data={articleOverview} />
      </div>

      {weeklyStats.length > 0 && (
        <div className="weekly-trends-section">
          <h2>Weekly Trends</h2>
          <div className="weekly-trends-chart">
            {/* Simple weekly trends display */}
            <div className="trend-cards">
              {weeklyStats.map((week, index) => (
                <div key={index} className="trend-card">
                  <div className="trend-period">
                    {week.week_start} to {week.week_end}
                  </div>
                  <div className="trend-stats">
                    <div className="trend-stat">
                      <span className="stat-label">TE:</span>
                      <span className="stat-value">{week.te_completed}</span>
                    </div>
                    <div className="trend-stat">
                      <span className="stat-label">CE:</span>
                      <span className="stat-value">{week.ce_completed}</span>
                    </div>
                    <div className="trend-stat total">
                      <span className="stat-label">Total:</span>
                      <span className="stat-value">{week.total_completed}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsDashboard;
